import { Component, OnInit, ViewEncapsulation, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import { filter } from 'rxjs';

import { untilComponentDestroyed, OnDestroyMixin } from '@w11k/ngx-componentdestroyed';
import { TreoNavigationItem } from 'tsui/@treo';
import { AccountService, AuthService } from 'app/core';
import { Account } from 'app/core/models';
import { FeatureFlag } from 'app/core/enums/feature-flag';
import { RelatedToType } from 'app/shared/enums';
import { ProjectInsightsService } from '../insights.service';
import { Compset, Project, Property } from 'app/modules/projects/projects.types';
import { ProjectInsightsAddCompsetComponent } from '../add-compset/add-compset.component';
import { NewPermission } from '@core/enums/permission';
import { Store } from '@ngxs/store';
import { UserState } from '@state/user/state';

@Component({
  selector: 'project-insights-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ProjectInsightsSidebarComponent extends OnDestroyMixin implements OnInit {
  private selectedCompsetId: number;
  private compsets: Compset[];
  private account: Account;
  public menuData: TreoNavigationItem[] = [];
  public properties: Property[] = [];
  public selectedProperty: Property;
  selectedProject: Project;

  constructor(
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _router: Router,
    private readonly _matDialog: MatDialog,
    private readonly _projectInsightsService: ProjectInsightsService,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _accountService: AccountService,
    private readonly _authService: AuthService,
    private readonly store: Store,
  ) {
    super();
    this.account = this._accountService.getCurrentAccount();
  }

  ngOnInit(): void {
    this.properties = this._projectInsightsService.properties;

    if (!this.selectedProperty) {
      this.selectedProperty = this.properties[0];
    }

    this._projectInsightsService.property$.pipe(untilComponentDestroyed(this)).subscribe((property: Property) => {
      if (property) this.selectedProperty = property;
      this._changeDetectorRef.markForCheck();
    });

    this.selectedProject = this._projectInsightsService.project;

    this._projectInsightsService.compsets$.pipe(untilComponentDestroyed(this)).subscribe((compsets) => {
      if (compsets) {
        this.selectedCompsetId = compsets[0]?.id;
        this.compsets = compsets;
      }
      this.configureMenu();
      this._changeDetectorRef.markForCheck();
    });

    this.configureMenu();
    this.checkRedirectToFirstPipeline(this._activatedRoute);

    this._router.events.pipe(untilComponentDestroyed(this)).subscribe((event) => {
      if (event instanceof NavigationEnd) this.checkRedirectToFirstPipeline(this._activatedRoute);
    });
  }

  private checkRedirectToFirstPipeline(route: ActivatedRoute): void {
    if (!route.snapshot.firstChild && this.properties.length > 0)
      this._router.navigate(['./', this.properties[0].id, 'market'], { relativeTo: route });
  }

  private openCompsetDialog(type: RelatedToType, compsetType = 'static'): void {
    this._matDialog
      .open(ProjectInsightsAddCompsetComponent, {
        panelClass: 'mailbox-compose-dialog',
        data: { type: type, compset_type: compsetType },
      })
      .afterClosed()
      .pipe(
        untilComponentDestroyed(this),
        filter((nil) => !!nil),
      )
      .subscribe((res) => {
        if (res) {
          this._router.navigate(['./', this.properties[0].id, 'comps', res.id], { relativeTo: this._activatedRoute });
        }
        this._changeDetectorRef.markForCheck();
      });
  }

  private configureMenu(): void {
    let menuLinks = [];
    let dealCompsetChildren = [];
    let sharedDealCompsetChildren = [];
    let propertyCompsetChildren = [];

    menuLinks.push({
      title: 'Market',
      type: 'basic',
      icon: 'settings',
      link: `../insights/${this.selectedProperty.id}/market`,
    });

    menuLinks.push({
      title: 'Tax Records',
      type: 'basic',
      icon: 'people',
      link: `../insights/${this.selectedProperty.id}/records`,
    });

    if (this.account.features.includes(FeatureFlag.INSIGHTS_ANALYTICS)) {
      menuLinks.push({
        title: 'Analytics',
        type: 'basic',
        icon: 'timeline',
        link: `../insights/${this.selectedProperty.id}/analytics`,
      });
    }

    if (this.selectedCompsetId && this.compsets) {
      for (let compset of this.compsets) {
        if (compset.object_type === 'Project' && compset.compset_type === 'static') {
          dealCompsetChildren.push({
            title: compset.title,
            type: 'basic',
            icon: 'memory',
            link: `../insights/${this.selectedProperty.id}/comps/${compset.id}`,
          });
        } else if (compset.object_type === 'Project' && compset.compset_type === 'dynamic') {
          sharedDealCompsetChildren.push({
            title: compset.title,
            type: 'basic',
            icon: 'memory',
            link: `../insights/${this.selectedProperty.id}/comps/${compset.id}`,
          });
        } else {
          propertyCompsetChildren.push({
            title: compset.title,
            type: 'basic',
            icon: 'memory',
            link: `../insights/${this.selectedProperty.id}/comps/${compset.id}`,
          });
        }
      }
    }

    dealCompsetChildren.push({
      title: 'Add Compset',
      type: 'basic',
      icon: 'add_circle_outline',
      function: () => this.openCompsetDialog(RelatedToType.Project, 'static'),
    });

    sharedDealCompsetChildren.push({
      title: 'Add Dynamic Compset',
      type: 'basic',
      icon: 'add_circle_outline',
      disabled: !this.canEditDeal(),
      function: () => this.openCompsetDialog(RelatedToType.Project, 'dynamic'),
    });

    menuLinks.push({
      title: 'Deal Dynamic Comps',
      type: 'group',
      children: sharedDealCompsetChildren,
    });

    menuLinks.push({
      title: 'Deal Static Comps',
      type: 'group',
      children: dealCompsetChildren,
    });

    propertyCompsetChildren.push({
      title: 'Add Compset',
      type: 'basic',
      icon: 'add_circle_outline',
      function: () => this.openCompsetDialog(RelatedToType.Property),
    });

    menuLinks.push({
      title: 'Property  Comps',
      type: 'group',
      children: propertyCompsetChildren,
    });

    this.menuData = [
      {
        title: 'Insights',
        type: 'group',
        children: menuLinks,
      },
    ];
  }

  public onSelectionChange(event: MatSelectChange): void {
    this._router.navigate(['../', 'insights', event.value.id, 'market'], { relativeTo: this._activatedRoute });
  }

  canEditDeal(): boolean {
    const hasEditPermission = this.store.selectSnapshot(UserState.hasPermission(NewPermission.DEALS_EDIT));
    return hasEditPermission && this.selectedProject.permissions?.is_editable;
  }
}
